import { lazy, Suspense } from "react";
import React from "react";
import { graphql, Script } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import PropTypes from "prop-types";
import CallToActionBlock from "../components/page/page-components/call-to-action-block";
import HtmlPageBlock from "../components/page/page-components/html-page-block";
import RelatedNewsBlock from "../components/page/page-components/related-news-block";
import PageBlockGroup from "../components/page/page-components/page-block-group";
import PageBlock from "../components/page/page-components/page-block";
import TestimonialBlock from "../components/shared/testimonial-block";
import HubSpotFormBlock from "../components/page/page-components/hubspot-form-block";
import YouVisitBlock from "../components/shared/youVisit-block";
import SimpleBlock from "../components/page/page-components/simple-block";
import PageHeader from "../components/page/page-header";
import PeopleBlock from "../components/page/page-components/people-block";
import SubtitleBlock from "../components/page/page-components/subtitle-block";
import PopularCoursesBlock from "../components/shared/popular-courses-block";
import MenuBlock from "../components/menu-block";
import { MenuProvider } from "../lib/store";
import { getLocalMenu } from "../lib/helpers";

function getStringFromBlock(block) {
  if (Array.isArray(block)) {
    return block
      .map((block) => {
        if (block._type !== "block" || !block.children) {
          return "";
        }
        return block.children.map((child) => child.text).join("");
      })
      .join("\n\n");
  }
  return ``;
}

const Page = ({ data, pageContext, location, path }) => {
  const localMenu = getLocalMenu(pageContext);
  function loadComponent(type, contentBlock, data) {
    const CallToActionBlock = lazy(() =>
      import("../components/page/page-components/call-to-action-block")
    );

    switch (type) {
      case "cta":
        return (
          <CallToActionBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );
      case "ctaReference":
        return (
          <CallToActionBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );

      case "htmlPageBlock":
        const HtmlPageBlock = lazy(() =>
          import("../components/page/page-components/html-page-block")
        );
        return (
          <HtmlPageBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );
      case "hubSpotFormBlockReference":
        const HubSpotFormBlock = lazy(() =>
          import("../components/page/page-components/hubspot-form-block")
        );
        return (
          <HubSpotFormBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );

      case "pageBlock":
        const PageBlock = lazy(() =>
          import("../components/page/page-components/page-block")
        );
        return (
          <PageBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );
      case "pageBlockGroup":
        const PageBlockGroup = lazy(() =>
          import("../components/page/page-components/page-block-group")
        );
        return (
          <PageBlockGroup
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );

      case "relatedNewsBlock":
        const RelatedNewsBlock = lazy(() =>
          import("../components/page/page-components/related-news-block")
        );
        return (
          <RelatedNewsBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );
      case "simpleBlock":
        const SimpleBlock = lazy(() =>
          import("../components/page/page-components/simple-block")
        );
        return (
          <SimpleBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );
      case "testimonialReference":
        const TestimonialBlock = lazy(() =>
          import("../components/shared/testimonial-block")
        );
        return (
          <TestimonialBlock
            key={contentBlock._key}
            props={contentBlock}
            section={data.page?.section?.urlPrefix}
            site={data.page?.section?.sectionSlug?.current}
          />
        );

      case "youVisitReference":
        const YouVisitBlock = lazy(() =>
          import("../components/shared/youVisit-block")
        );
        return (
          <YouVisitBlock
            key={contentBlock._key}
            props={contentBlock}
            site={data.page?.section?.sectionSlug?.current}
            section={data.page?.section?.urlPrefix}
          />
        );
    }
  }

  const section = data?.page.section;
  const contentBlocks = data?.page.contentBlocks;
  const pageHeader = data.page;
  const map = new Map();
  let Component = {};
  map.set("cta", CallToActionBlock);
  map.set("ctaReference", CallToActionBlock);
  map.set("htmlPageBlock", HtmlPageBlock);
  map.set("hubSpotFormBlockReference", HubSpotFormBlock);
  map.set("pageBlock", PageBlock);
  map.set("pageBlockGroup", PageBlockGroup);
  map.set("people", PeopleBlock);
  map.set("subtitleBlock", SubtitleBlock);
  map.set("popularCoursesBlock", PopularCoursesBlock);
  map.set("relatedNewsBlock", RelatedNewsBlock);
  map.set("simpleBlock", SimpleBlock);
  map.set("testimonialReference", TestimonialBlock);
  map.set("youVisitReference", YouVisitBlock);

  const metaImage = { url: "", height: "", width: "" };
  // TODO refactor with optional chaining
  if (data.page.image && data.page.image.asset) {
    metaImage.url = data.page.image.asset.url;
    if (data.page.image.asset.gatsbyImageData) {
      metaImage.height = data.page.image.asset.gatsbyImageData.height;
      metaImage.width = data.page.image.asset.gatsbyImageData.width;
    }
  }

  return (
    <MenuProvider menus={pageContext?.menus}>
      <Layout
        page={pageHeader}
        theme={data.page.section?.theme}
        section={data.page.section}
        banners={data.page.banners}
      >
        {data.page.noHeader !== true && (
          <PageHeader
            alias="pageHeader"
            section={section}
            pageHeader={pageHeader}
            location={location}
          />
        )}
        {localMenu.length > 0 && (
          <MenuBlock items={localMenu} id="local-menu" offset="77" />
        )}
        {contentBlocks.length > 0 &&
          contentBlocks.map((contentBlock) => {
            if (contentBlock?._type) {
              Component = map.get(contentBlock._type);
              return React.createElement(Component, {
                key: contentBlock._key,
                props: contentBlock,
                pageContext,
                section: data.page?.section?.urlPrefix,
                site: data.page?.section?.sectionSlug?.current,
              });
            }
          })}
      </Layout>
    </MenuProvider>
  );
};

export const pageQuery = graphql`
  query ($id: String!) {
    page: sanityPage(id: { eq: $id }) {
      _key
      headerTextFormat
      schemaType {
        type
      }
      showBreadcrumb
      _id
      title
      titleHorizontalAlignment
      path
      canonicalURL
      noHeader
      metaTitle
      metaKeywords
      metaDescription
      banners {
        _rawMessage(resolveReferences: { maxDepth: 10 })
        layout {
          background {
            color {
              code
            }
          }
          size {
            width
          }
        }
      }
      section {
        _id
        logo {
          asset {
            _id
          }
        }
        office {
          title
          shortName
          email
          phone
        }
        logoAlt {
          asset {
            _id
          }
        }
        title
        sectionSlug {
          current
        }
        menus {
          _rawItems(resolveReferences: { maxDepth: 6 })
        }
        urlPrefix
        theme {
          themeComponent
        }
      }
      _rawParentPage(resolveReferences: { maxDepth: 8 })
      image {
        hotspot {
          y
          x
          width
          height
        }
        crop {
          bottom
          left
          right
          top
        }
        asset {
          _id
          url
        }
      }
      videoPoster {
        hotspot {
          y
          x
          width
          height
        }
        crop {
          bottom
          left
          right
          top
        }
        asset {
          _id
          url
        }
      }
      videoMp4 {
        asset {
          url
        }
      }
      videoWebm {
        asset {
          url
        }
      }
      ...PageContentBlocks
    }
    relatedNewsPosts: allSanityNewsPost(limit: 3, sort: { publishedAt: DESC }) {
      edges {
        node {
          _key
          title
          featured
          slug {
            current
          }
          publishedAt
          _rawIntro(resolveReferences: { maxDepth: 10 })
          mainImage {
            hotspot {
              y
              x
              width
              height
            }
            crop {
              bottom
              left
              right
              top
            }
            asset {
              _id
            }
            altText
            caption
            attribution
          }
        }
      }
    }
  }
`;

Page.propTypes = {
  data: PropTypes.object.isRequired,
};
export const Head = ({ data, location, params, pageContext }) => {
  let schemaContent = "";
  if (data.page?.schemaType?.type === "FAQPage") {
    const mainEntity = [];
    for (const contentBlock of data.page?.contentBlocks) {
      if (contentBlock?.faqs?.length > 0) {
        for (const faqs of contentBlock.faqs) {
          mainEntity.push(
            `{
                        "@type": "Question",
                        "name": ${JSON.stringify(
                          getStringFromBlock(faqs._rawQuestion)
                        )},
                        "acceptedAnswer": {
                             "@type": "Answer",
                             "text": ${JSON.stringify(
                               getStringFromBlock(faqs._rawAnswer)
                             )}
                        }
                      }
                    `
          );
        }
      }
    }
    schemaContent = `"mainEntity": [${mainEntity.join(",")}]`;
  }

  return (
    <>
      {data.page?.schemaType?.type && (
        <script type="application/ld+json">
          {`{
              "@context": "http://schema.org",
              "@type": "${data.page.schemaType.type}",
              ${schemaContent}
          }`}
        </script>
      )}
      <Script id="hubspot-src" src="//js.hsforms.net/forms/embed/v2.js" />
      <Seo
        title={
          data?.page?.metaTitle !== null ? data.page.metaTitle : data.page.title
        }
        keywords={data.page.keywords}
        path={`${process.env.GATSBY_SITE_URL}${data.page.path}`}
        metaImage={data.page.metaImage}
        description={data.page.metaDescription}
        site={data.page?.section?.sectionSlug?.current}
        canonicalURL={data.page?.canonicalURL}
      />
    </>
  );
};

export default Page;
